import { CaretLeftIcon, CaretRightIcon, CircleNotchIcon, CornersOutIcon } from '@phosphor-icons/react'
import { type Track } from './api.ts'
import { useEditorControlsStore } from './store.ts'

interface TopRightEditorButtonProps {
  title: string
  onClick: () => void
  children: React.ReactNode
}

const TopRightEditorButton = ({ title, onClick, children }: TopRightEditorButtonProps) => (
  <button
    onClick={onClick}
    className="px-1 h-6 rounded hover:bg-[#333] flex items-center justify-center text-white opacity-50 hover:opacity-100"
    title={title}
  >
    {children}
  </button>
)

interface EditorControlsProps {
  currentTrack?: Track | null
  onLoadInA?: (track: Track) => void
  onLoadInB?: (track: Track) => void
  channelMode?: 'single' | 'dual'
}

export const EditorControls = ({
  currentTrack,
  onLoadInA,
  onLoadInB,
  channelMode = 'single',
}: EditorControlsProps) => {
  const isLoading = useEditorControlsStore(state => state.isLoading)
  const isFullscreen = useEditorControlsStore(state => state.isFullscreen)
  const toggleFullscreen = useEditorControlsStore(state => state.toggleFullscreen)

  const showDualButtons = channelMode === 'single' && currentTrack && onLoadInA && onLoadInB

  const loadInAButton = showDualButtons
    ? (
      <TopRightEditorButton
        title="Load in A"
        onClick={() => onLoadInA(currentTrack!)}
      >
        <CaretLeftIcon weight="regular" size={16} />
        <span className="text-[0.6rem] font-semibold -ml-1 pr-1">A</span>
      </TopRightEditorButton>
    )
    : null

  const loadInBButton = showDualButtons
    ? (
      <TopRightEditorButton
        title="Load in B"
        onClick={() => onLoadInB(currentTrack!)}
      >
        <span className="text-[0.6rem] font-semibold -mr-1 pl-1">B</span>
        <CaretRightIcon weight="regular" size={16} />
      </TopRightEditorButton>
    )
    : null

  const fullScreenButton = (
    <TopRightEditorButton
      title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
      onClick={toggleFullscreen}
    >
      <CornersOutIcon weight="regular" size={16} />
    </TopRightEditorButton>
  )

  const loadingSpinner = isLoading
    ? (
      <TopRightEditorButton title="Loading..." onClick={() => {}}>
        <CircleNotchIcon weight="regular" size={16} className="animate-spin" />
      </TopRightEditorButton>
    )
    : null

  return (
    <>
      {loadInAButton}
      {loadInBButton}
      {isLoading ? loadingSpinner : fullScreenButton}
    </>
  )
}
